/**
 * canvas-engine/presets/decompositionAdapters/generic.js
 *
 * The universal fallback adapter for normal / unknown sections. Generic
 * sections (hero, imageBanner, richText, featureGrid, productRail, faq,
 * testimonials, contactBlock, cartSummary) decompose faithfully with the core
 * engine's default vertical-stack layout, so this adapter delegates straight to
 * `buildSectionCanvas` with no family-specific tweaks.
 *
 * The adapter accepts EITHER a transcribed `GENERIC_PRESETS` descriptor OR a
 * section-type string looked up in `GENERIC_PRESETS`. Used directly for
 * `domain: 'generic'` descriptors and as the migration / dispatch fallback when
 * no family-specific descriptor exists: an unknown string still produces a
 * valid Canvas_Model (the empty descriptor decomposes to a minimal frame).
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 5.1, 5.6, 5.7
 */

import buildSectionCanvas, { REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { GENERIC_PRESETS } from '../presetData/genericPresets';

/** The generic / normal section types this adapter handles. */
export const GENERIC_TYPES = Object.freeze(handledTypesOf(GENERIC_PRESETS));

/** True when `sectionType` has a transcribed generic descriptor. */
export function handlesGenericType(sectionType) {
  return GENERIC_TYPES.includes(typeof sectionType === 'string' ? sectionType.trim() : sectionType);
}

/**
 * Decompose a generic / normal section into a normalized Canvas_Model.
 *
 * @param {(string|object)} sectionTypeOrData a `GENERIC_PRESETS` key /
 *   sectionType, or a transcribed generic preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options
 * @returns {object} a normalized Canvas_Model
 */
export function genericToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, GENERIC_PRESETS, options);
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  return buildSectionCanvas(data, opts);
}

export default genericToCanvas;
